import { NodeProps, useReactFlow } from '@xyflow/react';
import { Steps } from 'intro.js-react';
import React, { memo, useEffect, useRef, useState, useMemo } from 'react';
import '~/styles/introjs.css';
import useIntroTour from '@/hooks/useIntroTour';
import { useStrategyState, type StrategyNodeData } from '@/hooks/useStrategyState';
import styles from '@/styles/NodeStrategy.module.css';
import { RefreshCw, HelpCircle } from 'lucide-react';
import NodeBaseContainer from '../base/base-container/NodeBaseContainer';

const getQualityColor = (quality: string | undefined) => {
  switch (quality?.toLowerCase()) {
    case 'good': return 'bg-green-500';
    case 'moderate': return 'bg-yellow-500';
    case 'poor': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

const NodeStrategy: React.FC<NodeProps & { data: StrategyNodeData }> = (props) => {
  const { id, data, selected } = props;
  const { updateNodeData } = useReactFlow();
  const nodeBaseContainerRef = useRef<HTMLDivElement | null>(null);

  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(data.minimizeLevel || 'normal');
  const [isMinimized, setIsMinimized] = useState(data.isMinimized || false);

  const {
    isLocked,
    handleLock,
    handleMinimize,
    syncNodeData,
  } = useStrategyState(id, data, updateNodeData);

  const { introEnabled, setIntroEnabled, introSteps, introOptions } = useIntroTour(id, null);
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
      Array.isArray(data.assignedEmails) ? data.assignedEmails : (data.assignedEmails ? data.assignedEmails.trim().split(/\s+/).filter(Boolean) : [])
    );

  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' });
    } else {
      setMinimizeLevel(data.minimizeLevel);
      setIsMinimized(data.minimizeLevel !== 'normal');
    }
  }, [data.minimizeLevel, id, data, updateNodeData]);

  useEffect(() => {
    if (data.isMinimized !== undefined) {
      setIsMinimized(data.isMinimized);
    }
  }, [data.isMinimized]);

  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    if (data.currentSituationQuality) {
      minifiedInfo.current = data.currentSituationQuality;
    }
    
    if (data.targetSituationQuality) {
      minifiedInfo.target = data.targetSituationQuality;
    }
    
    if (data.middleStepsQuality) {
      minifiedInfo.steps = data.middleStepsQuality;
    }
    
    const qualityLevels = [data.currentSituationQuality, data.targetSituationQuality, data.middleStepsQuality].filter(Boolean);
    if (qualityLevels.length > 0) {
      const hasGood = qualityLevels.some(q => q?.toLowerCase() === 'good');
      const hasPoor = qualityLevels.some(q => q?.toLowerCase() === 'poor');
      const hasModerate = qualityLevels.some(q => q?.toLowerCase() === 'moderate');
      
      if (hasGood && !hasPoor) {
        minifiedInfo.overall = 'Good Strategy';
      } else if (hasPoor) {
        minifiedInfo.overall = 'Needs Improvement';
      } else if (hasModerate) {
        minifiedInfo.overall = 'Moderate Quality';
      } else {
        minifiedInfo.overall = 'Assessed';
      }
    }
    
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      syncNodeData({ minifiedInfo });
    }
  }, [data.currentSituationQuality, data.targetSituationQuality, data.middleStepsQuality, data.minifiedInfo, syncNodeData]);

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level);
    setIsMinimized(level !== 'normal');
    syncNodeData({ 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    });
  };

  const handleMinimizeLocal = () => {
    const newMinimized = !isMinimized;
    setIsMinimized(newMinimized);
    setMinimizeLevel(newMinimized ? 'medium' : 'normal');
    syncNodeData({ 
      isMinimized: newMinimized,
      minimizeLevel: newMinimized ? 'medium' : 'normal'
    });
  };

  const handleQualityUpdate = () => {
    syncNodeData({
      documentCommand: {
        'sysagent-cmd': 'Strategy-Process-Started',
        'sysagent-response': '',
      }
    });
  };

  const isProcessing = data.documentCommand?.processing || 
                      data.documentCommand?.['sysagent-cmd']?.includes('Started') || 
                      false;

  const getStatusMessage = () => {
    const cmd = data.documentCommand?.['sysagent-cmd'];
    const response = data.documentCommand?.['sysagent-response'];

    if (response?.includes('Error')) {
      return 'Error occurred during processing';
    }

    if (cmd?.includes('Process-Started')) {
      return 'Analyzing quality of connected nodes...';
    }

    if (response?.includes('Finished')) {
      return 'Quality assessment completed successfully';
    }

    return '';
  };

  const customButtons = [
    { 
      id: '1', 
      onClick: handleQualityUpdate, 
      icon: <RefreshCw size={16} />, 
      tooltip: 'Update Quality Assessment',
      disabled: isProcessing
    },
    { 
      id: '2', 
      onClick: () => setIntroEnabled(true), 
      icon: <HelpCircle size={16} />, 
      tooltip: 'Help & Guide',
      disabled: isProcessing
    },
  ];

  const QualityIndicator = ({ quality, details, label }: { quality?: string; details?: string; label: string }) => (
    <div className={`${styles.qualityIndicator} ${getQualityColor(quality)} text-white p-4 rounded-lg mb-4`}>
      <div className="font-semibold">{label}</div>
      <div className="flex items-center justify-between">
        <span>Quality: {quality || 'Not Assessed'}</span>
        {details && <span className="text-sm">({details})</span>}
      </div>
    </div>
  );

  const emailsArray = Array.isArray(assignedEmails) 
    ? assignedEmails 
    : (assignedEmails ? assignedEmails.trim().split(/\s+/).filter(Boolean) : []);

  const nodeStatus = useMemo(() => {
    if (isProcessing) return "loading";
    if (getStatusMessage().toLowerCase().includes("error")) return "error";
    if (
      data.currentSituationQuality ||
      data.targetSituationQuality ||
      data.middleStepsQuality
    ) return "success";
    return "initial";
  }, [
    isProcessing,
    getStatusMessage,
    data.currentSituationQuality,
    data.targetSituationQuality,
    data.middleStepsQuality
  ]);

  return (
    <NodeBaseContainer
      resizable={!isLocked && !isProcessing}
      minWidth={300}
      minHeight={200}
      id={id}
      data={data}
      isProcessing={isProcessing}
      status={nodeStatus}
      ref={nodeBaseContainerRef}
      nodeTitle={data.label || 'Strategy: Situational Awareness Mapping'}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel}
      onLockChange={handleLock}
      onMinimizeChange={handleMinimizeLocal}
      onMinimizeLevelChange={handleMinimizeLevelChange}
      onTitleChange={(title) => syncNodeData({ label: title })}
      customButtons={customButtons}
      assignedEmails={emailsArray}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt('Enter emails separated by space', emailsArray.join(' '))
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          setAssignedEmails(emailsArr)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
      showDebugInfo={false}
      selected={selected}
    >
      {minimizeLevel === 'normal' && (
        <div className={styles.nodeStrategy}>
          {!isMinimized && (
            <div className={styles.qualityIndicators}>
              <QualityIndicator
                label="Current Situation"
                quality={data.currentSituationQuality}
                details={data.currentSituationDetails}
              />
              <QualityIndicator
                label="Target Situation"
                quality={data.targetSituationQuality}
                details={data.targetSituationDetails}
              />
              <QualityIndicator
                label="Middle Steps"
                quality={data.middleStepsQuality}
                details={data.middleStepsDetails}
              />
            </div>
          )}
        </div>
      )}

      <Steps
        enabled={introEnabled && !isProcessing}
        steps={introSteps}
        initialStep={0}
        onExit={() => setIntroEnabled(false)}
        options={introOptions}
      />
    </NodeBaseContainer>
  );
};

export default memo(NodeStrategy);